/**
 * API Service for Wadakaroo
 * Centralized API calls using the axiosInstance with JWT interceptors.
 *
 * NOTE: No backend exists yet. These are placeholder endpoints
 * that define the expected API contract.
 */

import axios from 'axios';
import axiosInstance from './axiosInstance';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

// ============================================
// Auth Endpoints
// ============================================

/**
 * Login with email and password
 * keep the variable as username , it is a must
 */
export const loginApi = async (username, password) => {
    const response = await axiosInstance.post('/auth/login', { username, password });
    return response.data;
};

/**
 * Register a new user account with email
 */
export const registerUserApi = async (email, password) => {
    const response = await axiosInstance.post('/auth/register', { email, password });
    return response.data;
};

/**
 * Logout the current user
 */
export const logoutApi = async () => {
    const response = await axiosInstance.post('/auth/logout');
    return response.data;
};

/**
 * Refresh the access token using the stored refresh token.
 *
 * Uses a bare axios call (NOT axiosInstance) to avoid triggering the
 * response interceptor's recursive refresh logic if this call itself
 * 401s — the interceptor would otherwise try to refresh the refresh,
 * which is meaningless.
 *
 * Called proactively by useSessionManager ~2 minutes before the access
 * token's `exp`, so users actively working never see a 401 mid-action.
 * Reactive refresh (after an unexpected 401) still happens inside
 * axiosInstance's response interceptor.
 */
export const refreshTokenApi = async (refreshToken) => {
    const response = await axios.post(`${API_BASE_URL}/auth/token`, {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
    }, {
        headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
};

/**
 * Reset the current user's password.
 *
 * Called while the user holds a PASSWORD_RESET_REQUIRED scoped token —
 * the axios interceptor will attach that token as the Bearer credential.
 * Backend verifies currentPassword, replaces the hash, clears the
 * passwordResetRequired flag on the user record, and (server-side)
 * invalidates the restricted token.
 */
export const resetPasswordApi = async ({ currentPassword, newPassword }) => {
    // Path must match RestrictedTokenFilter.ALLOWED_PATHS_FOR_RESTRICTED_TOKENS
    // on the backend, otherwise the filter 403s the restricted token before
    // the controller is ever reached.
    const response = await axiosInstance.post('/auth/reset-password', {
        currentPassword,
        newPassword,
    });
    return response.data;
};

/**
 * Forgot password (logged-out): request a reset link by email.
 * The backend always responds 200 — it never reveals whether an account exists.
 */
export const forgotPasswordApi = async (email) => {
    const response = await axiosInstance.post('/auth/forgot-password', { email });
    return response.data;
};

/**
 * Set a new password using the single-use token from the reset-link email.
 */
export const resetPasswordWithTokenApi = async ({ token, newPassword }) => {
    const response = await axiosInstance.post('/auth/reset-password/confirm', {
        token,
        newPassword,
    });
    return response.data;
};

// ============================================
// User Profile Endpoints
// ============================================

/**
 * Complete user registration with full details
 */
export const completeRegistrationApi = async (userDetails) => {
    const response = await axiosInstance.post('/user/complete-registration', userDetails);
    return response.data;
};

/**
 * Get current user profile
 */
export const getUserProfileApi = async () => {
    const response = await axiosInstance.get('/user/profile');
    return response.data;
};

/**
 * Update user profile
 */
export const updateUserProfileApi = async (profileData) => {
    const response = await axiosInstance.put('/user/profile', profileData);
    return response.data;
};

// ============================================
// Organisation Endpoints
// ============================================

/**
 * Provision a new organisation for the current user
 */
export const provisionOrganisationApi = async (orgDetails) => {
    const response = await axiosInstance.post('/organisation/provision', orgDetails);
    return response.data;
};

/**
 * Get current user's organisation
 */
export const getOrganisationApi = async () => {
    const response = await axiosInstance.get('/organisation');
    return response.data;
};

/**
 * Update organisation details
 */
export const updateOrganisationApi = async (orgData) => {
    const response = await axiosInstance.put('/organisation', orgData);
    return response.data;
};

// ============================================
// UI View Profile Endpoints
// ============================================

/**
 * List all UI view profiles for the current org.
 * Returns an array of { uuid, name, description, allowedViews, createdAt, updatedAt }.
 */
export const listUiViewProfilesApi = async () => {
    const response = await axiosInstance.get('/ui-view-profiles');
    return response.data;
};

/**
 * Fetch one UI view profile by UUID.
 */
export const getUiViewProfileApi = async (uuid) => {
    const response = await axiosInstance.get(`/ui-view-profiles/${uuid}`);
    return response.data;
};

/**
 * Create a new UI view profile.
 *
 * @param {{ name: string, description?: string, allowedViews: string[] }} profile
 *        allowedViews is a list of UiViewKey names (e.g. ["BOOKINGS", "DASHBOARD"]).
 */
export const createUiViewProfileApi = async (profile) => {
    const response = await axiosInstance.post('/ui-view-profiles', profile);
    return response.data;
};

/**
 * Update an existing UI view profile.
 */
export const updateUiViewProfileApi = async (uuid, profile) => {
    const response = await axiosInstance.put(`/ui-view-profiles/${uuid}`, profile);
    return response.data;
};

/**
 * Delete a UI view profile. Fails with 400 if any user is still assigned to it.
 */
export const deleteUiViewProfileApi = async (uuid) => {
    const response = await axiosInstance.delete(`/ui-view-profiles/${uuid}`);
    return response.data;
};

/**
 * Reference list of every available UiViewKey with a human-readable label.
 * Used by the admin picker so the frontend doesn't duplicate the enum.
 */
export const listUiViewKeysApi = async () => {
    const response = await axiosInstance.get('/ui-view-profiles/keys');
    return response.data;
};

// ============================================
// Default export
// ============================================

const api = {
    loginApi,
    registerUserApi,
    logoutApi,
    refreshTokenApi,
    resetPasswordApi,
    forgotPasswordApi,
    resetPasswordWithTokenApi,
    completeRegistrationApi,
    getUserProfileApi,
    updateUserProfileApi,
    provisionOrganisationApi,
    getOrganisationApi,
    updateOrganisationApi,
    listUiViewProfilesApi,
    getUiViewProfileApi,
    createUiViewProfileApi,
    updateUiViewProfileApi,
    deleteUiViewProfileApi,
    listUiViewKeysApi,
};

export default api;